import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import {
  MUSIC_VIDEO_EFFECTS,
  type MusicVideoEffect,
  type MusicVideoEffectKind,
} from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { removeEffect, setEffectParam, setEffectTimes } from '../doc/effectOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { effectFamilyMessages, effectKindMessages, effectMessages } from '../messages/effectMessages';
import { formatTime } from '../timeline/timelineGeometry';
import { EffectParamControl } from './EffectParamControl';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The grey text of the empty text field of a card: what the card shows with no text. */
const TEXT_PLACEHOLDERS: Partial<Record<MusicVideoEffectKind, MessageDescriptor>> = {
  titleCard: effectMessages.textSongTitle,
  artistCard: effectMessages.textArtist,
  chapterCard: effectMessages.textChapter,
};

/**
 * The inspector of an effect (the look of the text inspector): its kind and family, its start and
 * end (set to the play head), one control for each param of its kind (`MUSIC_VIDEO_EFFECTS`), and
 * Remove. The controls come from the catalogue, so a new kind needs no UI code here.
 */
const EffectInspectorComponent: React.FC<{ effect: MusicVideoEffect }> = ({ effect }) => {
  const intl = useIntl();
  const { change, select, transport, readOnly } = useMusicVideoEditor();
  const spec = MUSIC_VIDEO_EFFECTS[effect.kind];
  const placeholder = TEXT_PLACEHOLDERS[effect.kind];

  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(effectKindMessages[effect.kind])}
        <span className={styles.inspectorSub}>{intl.formatMessage(effectFamilyMessages[spec.family])}</span>
      </h4>
      <div className={styles.inspectorGrid}>
        <span className={styles.rowLabel}>{intl.formatMessage(effectMessages.starts)}</span>
        <button
          type="button"
          className={styles.field}
          disabled={readOnly}
          onClick={() =>
            change((current) => setEffectTimes(current, effect.id, { startSec: transport.livePositionSec() }))
          }
        >
          {formatTime(effect.startSec, true)}
          <span className={styles.fieldUnit}>{intl.formatMessage(effectMessages.setToPlayHead)}</span>
        </button>
        <span className={styles.rowLabel}>{intl.formatMessage(effectMessages.ends)}</span>
        <button
          type="button"
          className={styles.field}
          disabled={readOnly}
          onClick={() =>
            change((current) => setEffectTimes(current, effect.id, { endSec: transport.livePositionSec() }))
          }
        >
          {formatTime(effect.endSec, true)}
          <span className={styles.fieldUnit}>{intl.formatMessage(effectMessages.setToPlayHead)}</span>
        </button>
      </div>
      <p className={styles.hint}>
        {intl.formatMessage(effectMessages.lengthHint, { min: spec.minSec, max: spec.maxSec })}
      </p>
      {spec.params.map((param) => (
        <EffectParamControl
          key={param.key}
          spec={param}
          value={effect.params[param.key]}
          disabled={readOnly}
          placeholder={param.type === 'text' && placeholder !== undefined ? intl.formatMessage(placeholder) : undefined}
          onChange={(value) =>
            change(
              (current) => setEffectParam(current, effect.id, param.key, value),
              `effect:${effect.id}:${param.key}`
            )
          }
        />
      ))}
      <button
        type="button"
        className={clsx(styles.ghostButton, styles.dangerButton)}
        disabled={readOnly}
        onClick={() => {
          change((current) => removeEffect(current, effect.id));
          select(null);
        }}
      >
        {intl.formatMessage(effectMessages.remove)}
      </button>
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(effectMessages.previewHint)}</p>
    </>
  );
};

export const EffectInspector = TypedMemo(EffectInspectorComponent);
